<template>
    <b-sidebar
        v-model="isOpen"
        id="agent-withdrawal-sidebar"
        right
        backdrop
        shadow
        :width="hasActiveRequest && activeRequest ? '80em' : '50em'"
        backdrop-variant="dark"
        no-close-on-backdrop
    >
        <template #title>
            <div>
                <strong v-if="!hasActiveRequest">Вывод средств</strong>
                <strong v-else>Заявка на вывод средств #{{ activeRequest && activeRequest.id }}</strong>
            </div>
        </template>
        
        <div class="p-3">
            <!-- Состояние загрузки -->
            <div v-if="checkingActiveRequest" class="text-center py-5">
                <b-spinner variant="primary" class="mb-3"></b-spinner>
                <p class="text-muted mb-0">Проверка активных заявок...</p>
            </div>
            
            <!-- Режим просмотра заявки -->
            <AgentWithdrawalRequestView
                v-else-if="hasActiveRequest && activeRequest"
                :withdrawal-request="activeRequest"
                :available-amount="availableAmount"
                @request-updated="handleRequestUpdated"
                @close="closeModal"
            />
            
            <!-- Режим создания новой заявки -->
            <div v-else>
            <!-- Информация о доступных средствах -->
            <b-card class="mb-4" bg-variant="light">
                <div class="d-flex align-items-center justify-content-between">
                    <div>
                        <h6 class="mb-1">Доступно к выводу</h6>
                        <div class="h4 text-success mb-0 font-weight-bold">{{ formatCurrency(availableAmount) }}</div>
                    </div>
                    <div class="text-right">
                        <small class="text-muted">Начислено: {{ formatCurrency(totalEarned) }}</small><br>
                        <small class="text-muted">Выплачено: {{ formatCurrency(totalWithdrawn) }}</small>
                    </div>
                </div>
            </b-card>

            <!-- Условия вывода -->
            <b-card class="mb-4" border-variant="secondary">
                <template #header>
                    <h6 class="mb-0 text-primary">
                        <i class="fas fa-exclamation-triangle mr-2"></i>
                        Условия вывода средств
                    </h6>
                </template>
                
                <div class="small">
                    <ul class="mb-0">
                        <li>Минимум <strong>1 завершенный заказ</strong></li>
                        <li>Подписанный и отправленный <strong>акт, который сформируется</strong></li>
                        <li>Налог при выплате: <strong>{{ taxRate }}%</strong> {{ taxDescription }}</li>
                        <li>Срок обработки (после получения акта): <strong>1-3 рабочих дня</strong></li>
                    </ul>
                </div>
            </b-card>

            <!-- Проверка условий вывода -->
            <b-alert 
                v-if="!canWithdraw" 
                variant="warning" 
                class="mb-4"
                show
            >
                <i class="fas fa-lock mr-2"></i>
                <strong>Вывод средств временно недоступен</strong><br>
                <div v-if="availableAmount <= 0" class="mt-2">
                    <i class="fas fa-exclamation-circle text-danger mr-1"></i>
                    Недостаточно средств для вывода.
                </div>
                <div v-if="hasActiveRequest" class="mt-2">
                    <i class="fas fa-info-circle text-info mr-1"></i>
                    У вас уже есть активная заявка на вывод средств. Дождитесь завершения текущей заявки.
                </div>
            </b-alert>


            <!-- Таблица доступных операций для вывода -->
            <b-card v-if="canWithdraw && availableSettlements && availableSettlements.length > 0" class="mb-4">
                <template #header>
                    <h6 class="mb-0">Доступные операции для вывода</h6>
                </template>
                <b-table
                    :items="availableSettlements"
                    :fields="settlementFields"
                    small
                    striped
                    hover
                    responsive
                    show-empty
                >
                    <template #cell(amount)="row">
                        <strong class="text-success">{{ formatCurrency(row.item.amount) }}</strong>
                    </template>
                    <template #cell(status)="row">
                        <b-badge :variant="getStatusVariant(row.item.status)">
                            {{ getStatusLabel(row.item.status) }}
                        </b-badge>
                    </template>
                </b-table>
            </b-card>

            <!-- Форма вывода -->
            <b-form @submit.prevent="submitWithdrawal">
                <b-form-group label="К выводу доступно:" label-for="withdrawal-amount">
                    <b-form-input
                        id="withdrawal-amount"
                        :value="formatCurrency(availableAmount)"
                        plaintext
                        readonly
                    ></b-form-input>
                    <div v-if="!hasActiveRequest">
                        <!-- <small class="text-muted">
                            К выводу доступно: {{ formatCurrency(availableAmount) }}
                        </small> -->
                    </div>
                    <div v-else class="mt-2">
                        <small class="text-info d-block">
                            <i class="fas fa-info-circle mr-1"></i>
                            Запрошено к выводу: <strong>{{ formatCurrency(activeRequest && activeRequest.amount ? activeRequest.amount : 0) }}</strong>
                        </small>
                        <small class="text-muted d-block mt-1">
                            Остаток на балансе: {{ formatCurrency(Math.max(0, availableAmount - (activeRequest && activeRequest.amount ? parseFloat(activeRequest.amount) : 0))) }}
                        </small>
                    </div>
                </b-form-group>

                <b-form-group label="Способ получения:" label-for="withdrawal-method">
                    <b-form-select
                        id="withdrawal-method"
                        v-model="withdrawalMethod"
                        :options="withdrawalMethods"
                        :disabled="!canWithdraw"
                    ></b-form-select>
                </b-form-group>

                <!-- <b-form-group label="Реквизиты для перевода:" label-for="withdrawal-details">
                    <b-form-textarea
                        id="withdrawal-details"
                        v-model="withdrawalDetails"
                        rows="3"
                        placeholder="Введите реквизиты для перевода (номер карты, счет и т.д.)"
                        :disabled="!canWithdraw"
                        :state="withdrawalDetailsState"
                    ></b-form-textarea>
                    <b-form-invalid-feedback v-if="!withdrawalDetailsState">
                        Пожалуйста, укажите реквизиты для перевода
                    </b-form-invalid-feedback>
                </b-form-group>
 -->
                <!-- Информация о сроке обработки -->
                <b-alert variant="info" class="mb-4" v-if="canWithdraw">
                    <div class="d-flex align-items-center">
                        <i class="fas fa-info-circle mr-2"></i>
                        <div>
                            <strong>Срок обработки:</strong> 1-3 рабочих дня после получения подписанного акта
                        </div>
                    </div>
                </b-alert>

                <!-- Кнопки -->
                <div class="d-flex justify-content-between">
                    <b-button 
                        variant="secondary" 
                        @click="closeModal"
                    >
                        Отмена
                    </b-button>
                    
                    <b-button 
                        type="submit"
                        variant="success"
                        :disabled="!canWithdraw || !isFormValid || isSubmitting"
                    >
                        <span v-if="isSubmitting">
                            <b-spinner small class="mr-1"></b-spinner>
                            Отправка...
                        </span>
                        <span v-else>
                            <i class="fas fa-file-invoice mr-1"></i>
                            Оформление запроса на вывод
                        </span>
                    </b-button>
                </div>
            </b-form>
            </div>
        </div>
    </b-sidebar>
</template>

<script>
import { API_AGENT_SETTLEMENTS, API_AGENT_WITHDRAWAL_REQUESTS } from "@/constants";
import { mapGetters } from 'vuex';
import AgentWithdrawalRequestView from './AgentWithdrawalRequestView.vue';

export default {
    name: 'AgentWithdrawalModal',
    components: {
        AgentWithdrawalRequestView
    },
    props: {
        open: {
            type: Boolean,
            default: false
        },
        availableAmount: {
            type: Number,
            default: 0
        },
        totalEarned: {
            type: Number,
            default: 0
        },
        totalWithdrawn: {
            type: Number,
            default: 0
        },
        hasCompletedDeals: {
            type: Boolean,
            default: false
        },
        agentCompanyId: {
            type: Number,
            default: null
        },
        agentCompanyType: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            withdrawalAmount: 0,
            withdrawalMethod: 'bank_account',
            withdrawalDetails: '',
            isSubmitting: false,
            availableSettlements: [],
            loadingSettlements: false,
            hasActiveRequest: false,
            checkingActiveRequest: false,
            activeRequest: null,
            withdrawalMethods: [
                // { value: 'bank_card', text: 'Банковская карта' },
                { value: 'bank_account', text: 'Банковский счет' },
                // { value: 'yoomoney', text: 'ЮMoney' },
                // { value: 'qiwi', text: 'QIWI Кошелек' }
            ],
            settlementFields: [
                { key: 'id', label: 'ID', sortable: true },
                { key: 'title', label: 'Название', sortable: true },
                { key: 'amount', label: 'Сумма', sortable: true },
                { key: 'status', label: 'Статус', sortable: true },
                { key: 'created_at', label: 'Дата создания', sortable: true }
            ]
        }
    },
    computed: {
        ...mapGetters('auth', ['currentUser']),
        isOpen: {
            get() {
                return this.open;
            },
            set(value) {
                this.$emit('update:open', value);
            }
        },
        // Проверка, активен ли выплатной период (первая или последняя неделя месяца)
        isWithdrawalPeriodActive() {
            const today = new Date();
            const day = today.getDate();
            const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
            
            // Первая неделя: 1-7 число
            const isFirstWeek = day >= 1 && day <= 7;
            // Последняя неделя: последние 7 дней месяца
            const isLastWeek = day >= (lastDayOfMonth - 6) && day <= lastDayOfMonth;
            
            return isFirstWeek || isLastWeek;
        },
        // Даты выплатного периода
        withdrawalPeriodStart() {
            const today = new Date();
            const day = today.getDate();
            const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
            
            if (day >= 1 && day <= 7) {
                // Первая неделя
                return new Date(today.getFullYear(), today.getMonth(), 1).toLocaleDateString('ru-RU', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            } else if (day >= (lastDayOfMonth - 6)) {
                // Последняя неделя
                return new Date(today.getFullYear(), today.getMonth(), lastDayOfMonth - 6).toLocaleDateString('ru-RU', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            } else {
                // Следующий период
                const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
                return nextMonth.toLocaleDateString('ru-RU', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            }
        },
        withdrawalPeriodEnd() {
            const today = new Date();
            const day = today.getDate();
            const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
            
            if (day >= 1 && day <= 7) {
                // Первая неделя
                return new Date(today.getFullYear(), today.getMonth(), 7).toLocaleDateString('ru-RU', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            } else if (day >= (lastDayOfMonth - 6)) {
                // Последняя неделя
                return new Date(today.getFullYear(), today.getMonth(), lastDayOfMonth).toLocaleDateString('ru-RU', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            } else {
                // Следующий период
                const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 7);
                return nextMonth.toLocaleDateString('ru-RU', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            }
        },
        // Проверка всех условий для вывода
        canWithdraw() {
            return this.availableAmount > 0 && !this.hasActiveRequest;
        },
        // Валидация формы
        isFormValid() {
            return this.availableAmount > 0 && !this.hasActiveRequest;
        },
        withdrawalDetailsState() {
            if (!this.withdrawalDetails) return null;
            return this.withdrawalDetails.trim().length > 0;
        },
        taxRate() {
            // Для физических лиц (fiz) - 13%, для ИП (ip) - 0%
            return this.agentCompanyType === 'fiz' ? 13 : 0;
        },
        taxDescription() {
            return this.agentCompanyType === 'fiz' ? '(физические лица)' : 
                   (this.agentCompanyType === 'ip' ? '(ИП)' : '');
        },
        // Следующая дата выплат
        nextPaymentDate() {
            const today = new Date();
            const currentMonth = today.getMonth();
            const currentYear = today.getFullYear();
            
            // Первая неделя месяца (1-7 число)
            const firstWeekStart = new Date(currentYear, currentMonth, 1);
            const firstWeekEnd = new Date(currentYear, currentMonth, 7);
            
            // Последняя неделя месяца
            const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
            const lastWeekStart = new Date(currentYear, currentMonth, lastDayOfMonth.getDate() - 6);
            const lastWeekEnd = new Date(currentYear, currentMonth, lastDayOfMonth.getDate());
            
            let nextDate;
            
            // Если сегодня в первой неделе
            if (today >= firstWeekStart && today <= firstWeekEnd) {
                nextDate = firstWeekEnd;
            }
            // Если сегодня в последней неделе
            else if (today >= lastWeekStart && today <= lastWeekEnd) {
                nextDate = lastWeekEnd;
            }
            // Если сегодня между неделями, то следующая выплата в последней неделе
            else if (today > firstWeekEnd && today < lastWeekStart) {
                nextDate = lastWeekStart;
            }
            // Если сегодня после последней недели, то следующая выплата в первой неделе следующего месяца
            else {
                nextDate = new Date(currentYear, currentMonth + 1, 7);
            }
            
            return nextDate.toLocaleDateString('ru-RU', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
            });
        },
        // Дедлайн для подачи заявки
        nextPaymentDeadline() {
            const today = new Date();
            const currentMonth = today.getMonth();
            const currentYear = today.getFullYear();
            
            // Первая неделя месяца (1-7 число)
            const firstWeekStart = new Date(currentYear, currentMonth, 1);
            const firstWeekEnd = new Date(currentYear, currentMonth, 7);
            
            // Последняя неделя месяца
            const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
            const lastWeekStart = new Date(currentYear, currentMonth, lastDayOfMonth.getDate() - 6);
            const lastWeekEnd = new Date(currentYear, currentMonth, lastDayOfMonth.getDate());
            
            let deadline;
            
            // Если сегодня в первой неделе
            if (today >= firstWeekStart && today <= firstWeekEnd) {
                deadline = new Date(firstWeekStart.getTime() - 3 * 24 * 60 * 60 * 1000); // за 3 дня до начала недели
            }
            // Если сегодня в последней неделе
            else if (today >= lastWeekStart && today <= lastWeekEnd) {
                deadline = new Date(lastWeekStart.getTime() - 3 * 24 * 60 * 60 * 1000); // за 3 дня до начала недели
            }
            // Если сегодня между неделями, то дедлайн за 3 дня до последней недели
            else if (today > firstWeekEnd && today < lastWeekStart) {
                deadline = new Date(lastWeekStart.getTime() - 3 * 24 * 60 * 60 * 1000);
            }
            // Если сегодня после последней недели, то дедлайн за 3 дня до первой недели следующего месяца
            else {
                deadline = new Date(currentYear, currentMonth + 1, 1);
                deadline.setDate(deadline.getDate() - 3);
            }
            
            return deadline.toLocaleDateString('ru-RU', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
            });
        }
    },
    watch: {
        open(newVal) {
            if (newVal) {
                // Сначала устанавливаем состояние загрузки
                this.checkingActiveRequest = true;
                this.resetForm();
                this.checkActiveRequest();
                this.loadAvailableSettlements();
            } else {
                // При закрытии сбрасываем состояние
                this.checkingActiveRequest = false;
                this.hasActiveRequest = false;
                this.activeRequest = null;
            }
        }
    },
    methods: {
        formatCurrency(value) {
            try {
                return new Intl.NumberFormat('ru-RU', { 
                    style: 'currency', 
                    currency: 'RUB', 
                    maximumFractionDigits: 0 
                }).format(Number(value || 0));
            } catch (e) {
                return `${value} ₽`;
            }
        },
        formatDate(dateString) {
            if (!dateString) return '-';
            try {
                const date = new Date(dateString);
                return date.toLocaleDateString('ru-RU', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            } catch (e) {
                return dateString;
            }
        },
        getStatusVariant(status) {
            const variants = {
                'pending': 'warning',
                'approved': 'success',
                'completed': 'primary',
                'rejected': 'danger'
            };
            return variants[status] || 'secondary';
        },
        getStatusLabel(status) {
            const labels = {
                'pending': 'Ожидает одобрения',
                'approved': 'Одобрено',
                'completed': 'Выполнено',
                'rejected': 'Отклонено'
            };
            return labels[status] || status;
        },
        async checkActiveRequest() {
            if (!this.agentCompanyId) return;
            
            this.checkingActiveRequest = true;
            try {
                // Получаем последний запрос на вывод средств (без фильтра по статусу)
                const response = await api.call('get', API_AGENT_WITHDRAWAL_REQUESTS, {
                    params: {
                        agent_company_id: this.agentCompanyId,
                        per_page: 1,
                        sortBy: 'id',
                        sortDirection: 'desc'
                    }
                });

                const requests = response.data.data || [];
                if (requests.length > 0) {
                    const lastRequest = requests[0];
                    // Показываем запрос, если у него нет даты отправки средств (funds_sent_at)
                    // Это означает, что средства еще не отправлены
                    if (!lastRequest.funds_sent_at && lastRequest.status !== 'funds_sent') {
                        this.hasActiveRequest = true;
                        this.activeRequest = lastRequest;
                    } else {
                        this.hasActiveRequest = false;
                        this.activeRequest = null;
                    }
                } else {
                    this.hasActiveRequest = false;
                    this.activeRequest = null;
                }
            } catch (error) {
                console.error('Ошибка проверки активных заявок:', error);
                this.hasActiveRequest = false;
                this.activeRequest = null;
            } finally {
                this.checkingActiveRequest = false;
            }
        },
        handleRequestUpdated(updatedRequest) {
            // Обновляем активную заявку после изменений в дочернем компоненте
            // Показываем запрос только если средства еще не отправлены
            if (updatedRequest && !updatedRequest.funds_sent_at && updatedRequest.status !== 'funds_sent') {
                this.activeRequest = updatedRequest;
                this.hasActiveRequest = true;
            } else {
                // Если средства отправлены, скрываем заявку
                this.activeRequest = null;
                this.hasActiveRequest = false;
            }
        },
        async loadAvailableSettlements() {
            if (!this.agentCompanyId) return;
            
            this.loadingSettlements = true;
            try {
                // Загружаем одобренные операции (debit), которые можно вывести
                const response = await api.call('get', API_AGENT_SETTLEMENTS, {
                    params: {
                        agent_company_id: this.agentCompanyId,
                        status: 'approved',
                        operation_type: 'debit',
                        per_page: 100
                    }
                });
                this.availableSettlements = response.data.data || [];
            } catch (error) {
                console.error('Ошибка загрузки доступных операций:', error);
                this.availableSettlements = [];
            } finally {
                this.loadingSettlements = false;
            }
        },
        resetForm() {
            // Устанавливаем сумму вывода равной доступной сумме (но не более 500000)
            this.withdrawalAmount = this.availableAmount > 0 ? this.availableAmount : 0;
            this.withdrawalMethod = 'bank_account';
            this.withdrawalDetails = '';
            this.isSubmitting = false;
        },
        closeModal() {
            this.$emit('update:open', false);
        },
        async submitWithdrawal() {
            if (!this.isFormValid || this.isSubmitting || !this.canWithdraw) return;
            
            this.isSubmitting = true;
            
            try {
                console.log(this.availableSettlements);
                // Собираем ID операций для вывода (берем все доступные одобренные операции)
                const settlementIds = this.availableSettlements
                    .filter(s => s.status === 'approved' && s.operation_type === 'debit')
                    .map(s => s.id);

                if (settlementIds.length === 0) {
                    this.$bvToast.toast('Нет доступных операций для вывода', {
                        title: 'Ошибка',
                        variant: 'danger',
                        solid: true
                    });
                    this.isSubmitting = false;
                    return;
                }

                // Создаем заявку на вывод через AgentWithdrawalRequest
                const withdrawalData = {
                    agent_company_id: this.agentCompanyId,
                    amount: this.availableAmount,
                    settlement_ids: settlementIds,
                    metadata: {
                        withdrawal_method: this.withdrawalMethod,
                        withdrawal_details: this.withdrawalDetails || '',
                        requested_at: new Date().toISOString()
                    }
                };
                
                const response = await api.call('post', API_AGENT_WITHDRAWAL_REQUESTS, withdrawalData);
                
                // Определяем ID и данные созданной заявки из ответа
                const createdRequest = (response && response.data && response.data.data)
                    ? response.data.data
                    : (response && response.data)
                        ? response.data
                        : null;
                
                if (!createdRequest || !createdRequest.id) {
                    console.error('Не удалось получить данные созданной заявки:', response);
                    throw new Error('Не удалось получить данные созданной заявки');
                }
                
                // Переключаем модал в режим просмотра заявки
                this.activeRequest = createdRequest;
                this.hasActiveRequest = true;
                
                // Обновляем данные с сервера, чтобы получить актуальные связи и вычисляемые поля
                await this.checkActiveRequest();
                
                // Уведомляем родительский компонент о создании заявки
                const emittedRequest = this.activeRequest || createdRequest;
                this.$emit('withdrawal-requested', emittedRequest);
                
                // Показываем уведомление об успехе
                this.$bvToast.toast('Заявка на вывод средств создана', {
                    title: 'Успешно',
                    variant: 'success',
                    solid: true
                });
                
                // После создания заявки форма больше не нужна
                this.resetForm();
                
            } catch (error) {
                console.error('Ошибка при отправке заявки на вывод:', error);
                
                let errorMessage = 'Произошла ошибка при отправке заявки. Попробуйте позже.';
                
                if (error.response && error.response.data) {
                    if (error.response.data.message) {
                        errorMessage = error.response.data.message;
                    } else if (error.response.data.error) {
                        if (typeof error.response.data.error === 'string') {
                            errorMessage = error.response.data.error;
                        } else if (error.response.data.error.message) {
                            errorMessage = error.response.data.error.message;
                        }
                    }
                }
                
                this.$bvToast.toast(errorMessage, {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
            } finally {
                this.isSubmitting = false;
            }
        }
    }
}
</script>

<style scoped>
.input-group-text {
    background-color: #f8f9fa;
    border-color: #ced4da;
}
</style>
